import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";

import BreakingNews from "../../components/BreakingNews";
import RightSidebar from "../../components/RightSidebar";
import NewsCard from "../../components/NewsCard";
import Profilecard from "../../components/Profilecard";

const NewsDetail = () => {
  const { id } = useParams();

  const [news, setNews] = useState(null);
  const [moreNews, setMoreNews] = useState([]);

  useEffect(() => {
    // 🔥 Scroll to top
    window.scrollTo({ top: 0, behavior: "auto" });

    // 🔥 VERY IMPORTANT: Stop any running speech before loading new page
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }

    // ================= FETCH MAIN NEWS =================
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}product/one/${id}`)
      .then((res) => {
        setNews(res.data);
      })
      .catch((err) => {
        console.error("Error fetching news:", err);
      });

    // ================= FETCH MORE NEWS =================
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}product`)
      .then((res) => {
        setMoreNews(res.data.slice(0, 6));
      })
      .catch((err) => {
        console.error("Error fetching more news:", err);
      });

    // 🔥 Cleanup on unmount
    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, [id]);

  if (!news) {
    return <div className="p-10 text-center">Loading...</div>;
  }

  // ── Helpers ──────────────────────────────────────────────────────────
  const backendBase = process.env.REACT_APP_BACKEND_URL
    ? process.env.REACT_APP_BACKEND_URL.replace('/api/v1/', '').replace('/api/v1', '')
    : '';

  // Build image URL safely: each path segment encoded individually
  // so folder separators (/) are NOT percent-encoded.
  const thumbnailUrl = news.thumbnail
    ? `${backendBase}/uploads/news/${news.thumbnail
        .replace(/\\/g, '/')
        .split('/')
        .map(encodeURIComponent)
        .join('/')}`
    : `${window.location.origin}/favicon.png`;

  // Strip HTML tags from description for OG / Twitter meta
  const plainDescription = news.description
    ? news.description.replace(/<[^>]*>/g, '').trim().slice(0, 160)
    : 'मध्य प्रदेश और छत्तीसगढ़ की ताज़ा खबरें हिंदी और बुंदेली में।';

  const pageUrl = `${window.location.origin}/news/${news.id}`;

  return (
    <>
      {/* ================= SEO ================= */}
      <Helmet>
        <title>{news.news_title} | Bundeli Chugli</title>

        <meta name="description" content={plainDescription} />

        {/* Open Graph — NOTE: These only work for real browsers.
            Social bots (Facebook/WhatsApp/Instagram) are intercepted
            at the server level (.htaccess → backend ogMiddleware)
            before React even loads. */}
        <meta property="og:type"              content="article" />
        <meta property="og:site_name"         content="Bundeli Chugli" />
        <meta property="og:title"             content={news.news_title} />
        <meta property="og:description"       content={plainDescription} />
        <meta property="og:url"               content={pageUrl} />
        <meta property="og:image"             content={thumbnailUrl} />
        <meta property="og:image:secure_url"  content={thumbnailUrl} />
        <meta property="og:image:width"       content="1200" />
        <meta property="og:image:height"      content="630" />
        <meta property="og:image:alt"         content={news.news_title} />

        {/* Twitter / X Card */}
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:title"       content={news.news_title} />
        <meta name="twitter:description" content={plainDescription} />
        <meta name="twitter:image"       content={thumbnailUrl} />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 pt-2 pb-6">
        <BreakingNews />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-4">
          {/* ================= MAIN ARTICLE ================= */}
          <div className="lg:col-span-9">
            <Profilecard data={news} />
          </div>

          {/* ================= SIDEBAR ================= */}
          <RightSidebar />
        </div>

        {/* ================= MORE NEWS ================= */}
        <section className="mt-12">
          <h2 className="text-xl font-bold mb-4 border-b pb-2 text-red-600">
            और खबरें
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {moreNews.map((item) => (
              <NewsCard
                key={item.id}
                title={item.news_title}
                image={`${backendBase}/uploads/news/${encodeURIComponent(item.thumbnail)}`}
                category={item.category}
                date={item.createdAt}
                fullNews={item.description}
                author="Bundeli Chugli"
                readTime="2 Min Read"
                link={`/news/${item.id}`}
                videoUrl={item.youtube_url}
              />
            ))}
          </div>
        </section>
      </div>
    </>
  );
};

export default NewsDetail;
